import { useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  EyeIcon,
  Loader2,
  MessageCircleIcon,
  PlusIcon,
  RefreshCw,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useAddContractByIDMutation,
  useGetAgentContractByIDQuery,
} from '@/apis/extended/agentApi';
import { Loader } from '@/components/Layout/Loader';
import { ProductContractSummary } from '@/components/Layout/SellerAgent/Negotiation/ProductContractSummary';
import { VoidContractButton } from '@/components/Negotiation/VoidContractButton';
import { Button } from '@/components/ui/button';
import {
  getContractAddDetailsRoute,
  getContractNegotiationRoute,
  getContractViewRoute,
} from '@/utils/contractRouting';
import { SellerAgentPropertyModal } from './Modal/Property';
import { AgentProductCard } from './Product/AgentProductCard';

const QualifiedContractPage = () => {
  const [inputId, setInputId] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const theme = useTheme();
  const navigate = useNavigate();
  const { agentId } = useParams();

  const [addContractByID] = useAddContractByIDMutation();

  const {
    data: property,
    refetch,
    isLoading: isContractLoading,
  } = useGetAgentContractByIDQuery(agentId ?? '', {
    skip: !agentId,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    pollingInterval: 0,
  });

  const verifyPropertyId = async () => {
    if (!agentId || !inputId) {
      setError('Missing agent ID or contract ID');
      return;
    }
    setIsLoading(true);
    const payload = {
      agentId,
      contractId: inputId,
    };
    try {
      await addContractByID(payload).unwrap();
      setInputId('');
      setError('');
      await refetch();
    } catch (error: any) {
      setError(error.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isContractLoading) {
    return <Loader isFull />;
  }

  const contracts = property as any;
  if (!contracts || !contracts.contracts || contracts.contracts.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <Box sx={{ maxWidth: '500px', width: '100%', textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 3 }}>
            No Contracts Found
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 3, color: theme.palette.greyShades.grey1 }}
          >
            You don't have any contracts yet. Please enter a Contract ID to add
            your first contract.
          </Typography>
          <TextField
            label="Contract ID"
            fullWidth
            size="small"
            value={inputId}
            onChange={(e) => {
              setInputId(e.target.value);
              setError('');
            }}
            helperText={error}
            error={!!error}
            disabled={isLoading}
            sx={{ mb: 2 }}
            placeholder="Enter contract ID here..."
          />
          <Button
            variant="outline"
            className="w-full mb-2"
            onClick={verifyPropertyId}
            disabled={isLoading}
          >
            {isLoading ? <Loader2 className="animate-spin" /> : 'Add Contract'}
          </Button>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.greyShades.grey1 }}
          >
            This will associate the contract with your agent account.
          </Typography>
        </Box>
      </Box>
    );
  } else {
    return (
      <>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
              mr: 4,
              mt: 2,
            }}
          >
            <Button variant="outline" onClick={() => setIsModalOpen(true)}>
              Add Another Contract
            </Button>
          </Box>
          <Typography variant="h4" sx={{ mb: 2 }}>
            Your Contracts
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.greyShades.grey1 }}
          >
            You have {contracts.contracts.length} contracts available.
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            justifyContent: 'center',
            alignItems: 'center',
            mt: 4,
          }}
        >
          {contracts.contracts.map((item: any) => {
            const isRatified =
              item.contractDetails.contractStatus === 'ratified' ||
              item.contractDetails.isRatified === true;
            const isVoided = item.contractDetails.contractStatus === 'voided';

            return (
              <Box
                key={item._id}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  gap: '32px',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  width: '100%',
                  maxWidth: '1200px',
                }}
              >
                {/* Product Card */}
                <Box sx={{ flex: '1', maxWidth: '500px', width: '100%' }}>
                  <AgentProductCard
                    product={item.propertyDetails}
                    navigateTo={`/agent/product/${item.propertyDetails?._id}`}
                  />
                </Box>

                {/* Contract Summary with Action Buttons */}
                <Box
                  sx={{ flex: '0 0 400px', maxWidth: '400px', width: '100%' }}
                >
                  {/* ProductContractSummary now has the status badge built-in */}
                  <ProductContractSummary
                    product={item.product}
                    contractId={item.contractDetails?._id}
                    userRole="agent"
                    contractStatus={item.contractDetails.contractStatus}
                    isRatified={item.contractDetails.isRatified}
                  />
                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      justifyContent: 'center',
                    }}
                  >
                    {/* Contract Status-Based Button Logic */}
                    {/* Show different buttons based on contract status */}
                    {isRatified ? (
                      // Show Property Dashboard only when ratified - NO REJECT BUTTON
                      <Button
                        variant="default"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => {
                          navigate(
                            `/agent/property-dashboard/${item.contractDetails._id}`
                          );
                        }}
                      >
                        <i className="fa-light fa-chart-line mr-2" />
                        Property Dashboard
                      </Button>
                    ) : isVoided ? (
                      // Show message for voided contracts - NO REJECT BUTTON
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: '#fee2e2',
                          borderRadius: '8px',
                          border: '1px solid #dc2626',
                          textAlign: 'center',
                          width: '100%',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: '#991b1b', fontWeight: 600 }}
                        >
                          This contract has been rejected
                        </Typography>
                        {item.contractDetails.voidedAt && (
                          <Typography
                            variant="caption"
                            sx={{ color: '#991b1b', display: 'block', mt: 0.5 }}
                          >
                            Voided on{' '}
                            {new Date(
                              item.contractDetails.voidedAt
                            ).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <>
                        {/* Active contracts - Show Edit Contract Details */}
                        <Button
                          variant="default"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => {
                            navigate(
                              `/contract/agent/${item.contractDetails._id}`
                            );
                          }}
                        >
                          <i className="fa-light fa-edit mr-2" />
                          Edit Contract Details
                        </Button>

                        {/* Show Reject button ONLY for active contracts */}
                        <VoidContractButton
                          contractId={item.contractDetails._id}
                          voidedBy="agent"
                          contractStatus={item.contractDetails.contractStatus}
                          isRatified={item.contractDetails.isRatified}
                          onVoidSuccess={() => refetch()}
                        />

                        {/* Show Negotiate button for active contracts */}
                        <Button
                          variant="outline"
                          onClick={() => {
                            const negotiateRoute = getContractNegotiationRoute(
                              item.contractId?._id || item.contractDetails._id,
                              'agent'
                            );
                            navigate(negotiateRoute);
                          }}
                        >
                          <i className="fa-light fa-handshake mr-2" />
                          Negotiate Contract
                        </Button>
                      </>
                    )}

                    <Button
                      variant="outline"
                      onClick={() => {
                        navigate(`/view-contract/${item.contractDetails._id}`);
                      }}
                    >
                      <i className="fa-light fa-file-contract mr-2" />
                      View Contract
                    </Button>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        <SellerAgentPropertyModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          agentId={agentId}
        />
      </>
    );
  }
};

export default QualifiedContractPage;
